<template>
  <div class="min-h-screen bg-gradient-to-b from-white to-gray-50 text-gray-900">
    <div class="container mx-auto px-4 py-16 max-w-4xl">
      <!-- Logo Section -->
      <div class="text-center mb-8">
        <img src="/logo.svg" alt="Lessons Learned" class="mx-auto h-24 w-auto mb-8" />
      </div>

      <!-- Header Section -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-bold mb-6 text-ll-blue">
          Integrity Activities Benchmarking Framework
        </h1>
        <h2 class="text-2xl md:text-3xl font-semibold mb-4 text-gray-700">
          A Practical Resource for IFI Integrity Professionals
        </h2>
        <p class="text-xl text-gray-600">
          Download your framework for benchmarking, reflecting on, and planning integrity activities across international financial institutions.
        </p>
      </div>

      <!-- Hero Image -->
      <div class="mb-8 max-w-2xl mx-auto">
        <img
          src="/ll-people-mono.jpg"
          alt="Lessons Learned Compliance Team"
          class="w-full rounded-lg shadow-md"
        />
      </div>

      <!-- Success Message -->
      <div v-if="submitted" class="bg-green-50 border border-green-500 rounded-lg p-8 mb-8 text-center">
        <h2 class="text-2xl font-semibold mb-4 text-green-700">Thank you!</h2>
        <p class="text-lg mb-4 text-gray-700">Please check your email for the download link.</p>
        <p class="text-gray-600">The framework guide will be in your inbox shortly.</p>
      </div>

      <!-- Lead Capture Form -->
      <div v-if="!submitted" class="bg-white rounded-lg p-8 md:p-12 shadow-xl border border-gray-200">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Name -->
          <div>
            <label for="name" class="block text-sm font-semibold mb-2 text-gray-700">
              Name <span class="text-ll-red">*</span>
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              required
              class="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-ll-blue focus:ring-2 focus:ring-ll-blue transition-colors"
              placeholder="Enter your name"
            />
          </div>

          <!-- Email Address -->
          <div>
            <label for="email" class="block text-sm font-semibold mb-2 text-gray-700">
              Email <span class="text-ll-red">*</span>
            </label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              class="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-ll-blue focus:ring-2 focus:ring-ll-blue transition-colors"
              placeholder="Enter your email"
            />
          </div>

          <!-- Phone (Optional) -->
          <div>
            <label for="phone" class="block text-sm font-semibold mb-2 text-gray-700">
              Phone <span class="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              v-model="formData.phone"
              class="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-ll-blue focus:ring-2 focus:ring-ll-blue transition-colors"
              placeholder="Enter your phone number"
            />
          </div>

          <!-- Opt-in Checkbox -->
          <div class="flex items-start space-x-3">
            <input
              type="checkbox"
              id="optin"
              v-model="formData.optin"
              required
              class="mt-1 h-5 w-5 text-ll-blue border-gray-300 rounded focus:ring-ll-blue focus:ring-2 cursor-pointer"
            />
            <label for="optin" class="text-sm text-gray-700 cursor-pointer">
              I agree to receive updates and insights on financial crime compliance from Lessons Learned. <span class="text-ll-red">*</span>
            </label>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="bg-red-50 border border-ll-red rounded-lg p-4">
            <p class="text-ll-red text-sm">{{ errorMessage }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full bg-ll-red hover:bg-red-700 text-white text-lg font-semibold py-4 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSubmitting ? 'Submitting...' : 'Download the Framework' }}
          </button>

          <!-- Compliance Note -->
          <p class="text-xs text-gray-500 text-center mt-6">
            We respect your privacy. Your details will be used to share relevant updates and financial crime compliance insights. You can unsubscribe at any time.
          </p>
        </form>
      </div>

      <!-- What's Inside Section -->
      <div v-if="!submitted" class="mt-12 bg-white rounded-lg p-8 shadow-lg border border-gray-200">
        <h2 class="text-2xl font-bold mb-6 text-ll-blue text-center">
          What's Inside the Framework
        </h2>
        <div class="grid md:grid-cols-2 gap-4 text-gray-700">
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>The Integrity Operating Model across 10 domains</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Governance & strategic alignment guidance</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Integrity risk management frameworks</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Due diligence & preventive controls</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Culture, conduct & integrity leadership</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Monitoring, detection & supervision</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Investigations & enforcement</span>
          </div>
          <div class="flex items-start">
            <span class="text-ll-red mr-3 text-xl font-bold">✓</span>
            <span>Integrity maturity overview & planning template</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'IntegrityFrameworkDownload',
  data() {
    return {
      formData: {
        name: '',
        email: '',
        phone: '',
        optin: false,
      },
      isSubmitting: false,
      submitted: false,
      errorMessage: '',
    };
  },
  methods: {
    async handleSubmit() {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const response = await axios.post('/api/submit-integrity-framework-form', this.formData);

        if (response.data.success) {
          this.submitted = true;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error('Form submission error:', error);
        if (error.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'There was an error submitting your form. Please try again.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
